<template>
  <div v-if="currentStep === 1" class="p-4">
    <h4 class="mb-3">Quittungstyp wählen</h4>
    <p class="text-muted mb-4">
      Wähle, ob du eine persönliche oder Bereich-Quittung erstellen möchtest.
    </p>

    <div class="mb-4 p-4">
      <label class="form-label">Quittungstyp wählen</label>
      <div class="row">
        <div class="col-md-6 mb-3">
          <div
            class="template-card team-card personal-card"
            :class="{
              selected: !selectedTeamId,
              disabled: personalCoins < 1,
            }"
            @click="selectPersonal"
          >
            <div class="template-card-header">
              <div class="template-card-icon">
                <font-awesome-icon icon="user" />
              </div>
              <div class="template-card-badge">
                <font-awesome-icon icon="coins" class="text-warning" />
              </div>
            </div>
            <div class="template-card-body">
              <h6 class="template-card-title">Persönliche Quittung</h6>
              <p class="template-card-description">Für private Zwecke</p>
              <div class="template-card-status">
                <small class="fw-bold text-warning">
                  {{ personalCoins }} Quittungen verfügbar
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bereiche Loading State -->
    <div v-if="teamsLoading" class="text-center py-5">
      <div class="loading-animation">
        <div class="loading-spinner">
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
        </div>
        <h5 class="mt-3 mb-2">Bereiche werden geladen</h5>
        <p class="text-muted mb-0">Bitte warte einen Moment...</p>
      </div>
    </div>

    <!-- Bereiche List -->
    <div v-else-if="hasTeams">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <label class="form-label mb-0">Oder Bereich-Quittung erstellen</label>
        <small class="text-muted" v-if="showTeamsPagination">
          {{ teamsPaginationData?.total || 0 }}
          {{ (teamsPaginationData?.total || 0) === 1 ? 'Bereich' : 'Bereiche' }} gefunden
        </small>
      </div>

      <!-- Search Input (only if more than 3 bereiche) -->
      <div class="mb-3" v-if="showTeamsSearch">
        <div class="input-group">
          <span class="input-group-text">
            <font-awesome-icon icon="search" />
          </span>
          <input
            type="text"
            class="form-control"
            placeholder="Bereich suchen..."
            v-model="searchQuery"
          />
          <button
            v-if="searchQuery"
            class="btn btn-outline-secondary"
            type="button"
            @click="clearSearch"
          >
            <font-awesome-icon icon="times" />
          </button>
        </div>
      </div>

      <!-- Bereiche Grid -->
      <div class="row">
        <div v-for="team in paginatedTeams" :key="team.id" class="col-md-6 col-lg-4 mb-3">
          <div
            class="template-card team-card"
            :class="{
              selected: selectedTeamId === team.id,
              disabled: (team.account?.amount || 0) < 1,
            }"
            @click="selectTeam(team.id)"
          >
            <div class="template-card-header">
              <div class="template-card-icon">
                <font-awesome-icon icon="users" />
              </div>
              <div class="template-card-badge">
                <font-awesome-icon icon="coins" class="text-warning" />
              </div>
            </div>
            <div class="template-card-body">
              <h6 class="template-card-title">{{ team.name }}</h6>
              <p class="template-card-description">Bereich-Quittung</p>
              <div class="template-card-status">
                <small class="fw-bold text-warning">
                  {{ team.account?.amount || 0 }} Quittungen verfügbar
                </small>
                <div v-if="team.taxRate && team.taxRate !== '0'" class="mt-1">
                  <small class="text-info">
                    <font-awesome-icon icon="percentage" class="me-1" />
                    MwSt: {{ team.taxRate }}%
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination (only if more than 3 bereiche) -->
      <div v-if="showTeamsPagination" class="d-flex justify-content-center mt-3">
        <nav aria-label="Bereiche Pagination">
          <ul class="pagination pagination-sm">
            <li class="page-item" :class="{ disabled: teamsPaginationData?.page === 1 }">
              <button
                class="page-link"
                @click="prevTeamsPage"
                :disabled="teamsPaginationData?.page === 1"
              >
                <font-awesome-icon icon="chevron-left" />
                <span class="visually-hidden">Vorherige</span>
              </button>
            </li>
            <li
              v-for="page in totalTeamPages"
              :key="page"
              class="page-item"
              :class="{ active: teamsPaginationData?.page === page }"
            >
              <button class="page-link" @click="goToTeamsPage(page)">
                {{ page }}
              </button>
            </li>
            <li
              class="page-item"
              :class="{ disabled: teamsPaginationData?.page === totalTeamPages }"
            >
              <button
                class="page-link"
                @click="nextTeamsPage"
                :disabled="teamsPaginationData?.page === totalTeamPages"
              >
                <font-awesome-icon icon="chevron-right" />
                <span class="visually-hidden">Nächste</span>
              </button>
            </li>
          </ul>
        </nav>
      </div>

      <!-- No Bereiche Found Message -->
      <div
        v-if="filteredTeamsLength === 0 && searchQuery"
        class="text-center py-3 text-muted"
      >
        <font-awesome-icon icon="search" class="me-2" />
        Keine Bereiche gefunden für "{{ searchQuery }}"
        <button class="btn btn-link btn-sm" @click="clearSearch">
          Suche zurücksetzen
        </button>
      </div>
    </div>

    <!-- No Bereiche Message -->
    <div v-else class="text-center py-3 text-muted">
      <font-awesome-icon icon="info-circle" class="me-2" />
      Du bist noch kein Mitglied in einem Team. Erstelle ein Team oder lasse dich einladen.
    </div>
  </div>
</template>

<script setup>
import { computed, inject } from 'vue'
import { ReceiptWizardSymbol } from '../../tokens'

const wizard = inject(ReceiptWizardSymbol)

if (!wizard) {
  throw new Error('StepTeamSelection requires ReceiptWizard context')
}

const currentStep = wizard.currentStep
const formData = wizard.formData
const selectedTeamId = computed(() => formData.value.teamId)
const personalCoins = computed(() => wizard.authStore.user?.account?.amount || 0)
const teamsLoading = wizard.teamsLoading
const userTeams = wizard.userTeams
const teamsPaginationData = wizard.teamsPaginationData
const showTeamsPagination = wizard.showTeamsPagination
const showTeamsSearch = wizard.showTeamsSearch
const paginatedTeams = wizard.paginatedTeams
const totalTeamPages = wizard.totalTeamPages
const filteredTeams = wizard.filteredTeams
const teamSearchQuery = wizard.teamSearchQuery

const hasTeams = computed(() => (userTeams.value || []).length > 0)
const filteredTeamsLength = computed(() => filteredTeams.value?.length || 0)

const searchQuery = computed({
  get: () => teamSearchQuery.value,
  set: (value) => {
    teamSearchQuery.value = value
  },
})

const selectPersonal = wizard.selectPersonal
const selectTeam = wizard.selectTeam
const goToTeamsPage = wizard.goToTeamsPage
const prevTeamsPage = wizard.prevTeamsPage
const nextTeamsPage = wizard.nextTeamsPage

const clearSearch = () => {
  teamSearchQuery.value = ''
}
</script>
